import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../../../StyledTypography';
import { isFileFieldType } from '@iqvia-technology-solutions/formcrafter';
import FieldValueRenderer from './FieldValueRenderer';
import FieldEditor from '../FieldEditor/FieldEditor';
import FieldActionsSection from '../../FieldActions';
import QuickCommentInput from '../../Comments/QuickCommentInput';
import styles from '../FormCrafterListView.module.css';

/**
 * FieldRow - Atomic component for rendering a single field row in ListView
 *
 * Follows atomic design - composes smaller components into a reusable row.
 *
 * @param {Object} props
 * @param {string} props.fieldKey - Unique key for this field
 * @param {string} props.label - Field label
 * @param {*} props.value - Current field value
 * @param {*} props.displayValue - Value to display (may differ from value if edited)
 * @param {Object} props.field - Field schema
 * @param {string|number} props.subformId - Subform ID
 * @param {Object} props.subformData - Subform data context
 * @param {Array} props.files - File metadata for this field
 * @param {boolean} props.showHiddenFields - Whether to show hidden fields
 * @param {boolean} props.showRequiredIndicator - Whether to show required asterisk
 * @param {boolean} props.isRequired - Whether field is required
 * @param {boolean} props.isEditing - Whether field is being edited
 * @param {*} props.draftValue - Current draft value during editing
 * @param {Object} props.editActions - Edit action handlers { onStart, onCancel, onConfirm, onUpdate }
 * @param {Object} props.commentProps - Comment-related props
 */
const FieldRow = ({
  fieldKey,
  label,
  value,
  displayValue,
  field,
  subformId,
  subformData,
  files,
  serverFiles = [],
  stagedFiles = [],
  onServerFileDelete,
  onStagedFileUpload,
  onStagedFileDelete,
  eagerUploadEnabled = false,
  deletedFileIds = [],
  showHiddenFields = false,
  showRequiredIndicator = false,
  isRequired = false,
  isEditing = false,
  draftValue,
  editActions = {},
  commentProps = {},
  allowDeletedFiles = false,
}) => {
  const { t } = useTranslation();
  const {
    comments = [],
    enableAddComment = false,
    enableFieldEdit = false,
    canEdit = false,
    onAddComment,
    activeQuickCommentField,
    activeCommentsModalField,
    onQuickCommentToggle,
    onCommentsModalToggle,
    onCloseQuickComment,
    onCommentAdded,
    // Field waves props
    requestId,
    formId,
    enableFieldWaves = false,
    hasWaves = false,
    fieldKeyOverride = null,
    fieldWavesKeyOverride = null,
    fieldIdOverride = null,
    onEditFieldOverride = null,
    // Hidden field indicator
    isHiddenField = false,
    // Comment edit/delete props
    requestProgress,
    requestDateEnded,
    currentUserEmail,
    onCommentUpdated,
    onCommentDeleted,
  } = commentProps;

  const { onStart, onCancel, onConfirm, onUpdate } = editActions;

  const showActions =
    comments.length > 0 ||
    enableAddComment ||
    (enableFieldEdit && canEdit) ||
    enableFieldWaves ||
    hasWaves;

  const handleEditField = useCallback(() => {
    if (!onStart) return;
    // For file fields with previously edited File objects, use them; otherwise empty array
    // This preserves new uploads when re-opening editor after confirm
    const isFileField = isFileFieldType(field?.type);
    const isRepeaterField =
      field?.type === 'repeater' || field?.type === 'repeatable';
    const hasEditedFiles =
      isFileField &&
      Array.isArray(displayValue) &&
      displayValue.some((v) => v instanceof File);

    let initialValue;
    if (isFileField) {
      initialValue = hasEditedFiles ? displayValue : [];
    } else if (isRepeaterField) {
      // For repeater fields, use value (array) not displayValue (string)
      initialValue = Array.isArray(value) ? value : [];
    } else {
      initialValue = displayValue;
    }
    onStart(initialValue);
  }, [onStart, field?.type, displayValue, value]);

  // Single source of truth: filter files by deletedFileIds once, use everywhere
  const filteredFiles = useMemo(() => {
    if (!files || files.length === 0) return files;
    if (!deletedFileIds || deletedFileIds.length === 0) return files;
    const deletedIdsSet = new Set(deletedFileIds.map(String));
    return files.filter((f) => !deletedIdsSet.has(String(f.id)));
  }, [files, deletedFileIds]);

  // Filter server files by deletedFileIds for the editor
  // Use String comparison to handle type mismatches (number vs string)
  const filteredServerFiles = useMemo(() => {
    if (!serverFiles || serverFiles.length === 0) return serverFiles;
    if (!deletedFileIds || deletedFileIds.length === 0) return serverFiles;
    const deletedIdsSet = new Set(deletedFileIds.map(String));
    return serverFiles.filter((f) => !deletedIdsSet.has(String(f.id)));
  }, [serverFiles, deletedFileIds]);

  return (
    <div className={styles.fieldRowContainer}>
      <div className={styles.fieldRow}>
        <div className={styles.fieldKey}>
          {showRequiredIndicator && isRequired && (
            <span className={styles.requiredAsterisk}>*</span>
          )}
          <Typography variant="bodySmall" className={styles.keyText}>
            {label}:
          </Typography>
        </div>
        <div className={styles.fieldValueContainer}>
          <div className={styles.fieldValueRow}>
            <div className={styles.fieldValueContent}>
              {isEditing ? (
                <FieldEditor
                  field={field}
                  value={draftValue}
                  onChange={onUpdate}
                  onConfirm={onConfirm}
                  onCancel={onCancel}
                  existingFiles={filteredServerFiles}
                  onServerFileDelete={onServerFileDelete}
                  formId={formId}
                  subformId={subformId}
                  onStagedFileUpload={onStagedFileUpload}
                  onStagedFileDelete={onStagedFileDelete}
                  eagerUploadEnabled={eagerUploadEnabled}
                  initialStagedFiles={stagedFiles}
                />
              ) : (
                <FieldValueRenderer
                  subformId={subformId}
                  fieldId={field.id}
                  value={displayValue}
                  fieldType={field.type}
                  field={field}
                  subformData={subformData}
                  files={filteredFiles}
                  allowDeletedFiles={allowDeletedFiles}
                  showHiddenFields={showHiddenFields}
                />
              )}
            </div>
            {!isEditing && showActions && (
              <div className={styles.fieldActionsWrapper}>
                <FieldActionsSection
                  requestId={requestId}
                  formId={formId}
                  subformId={subformId}
                  fieldId={fieldIdOverride || field.id}
                  fieldLabel={label}
                  comments={comments}
                  onAddComment={onAddComment}
                  enableAddComment={enableAddComment}
                  enableFieldEdit={enableFieldEdit && canEdit}
                  enableFieldWaves={enableFieldWaves}
                  hasWaves={hasWaves}
                  onEditField={onEditFieldOverride || handleEditField}
                  fieldKeyOverride={fieldKeyOverride}
                  fieldWavesKeyOverride={fieldWavesKeyOverride}
                  isQuickCommentOpen={activeQuickCommentField === fieldKey}
                  onQuickCommentToggle={(isOpen) =>
                    onQuickCommentToggle &&
                    onQuickCommentToggle(fieldKey, isOpen)
                  }
                  isCommentsModalOpen={activeCommentsModalField === fieldKey}
                  onCommentsModalToggle={(isOpen) =>
                    onCommentsModalToggle &&
                    onCommentsModalToggle(fieldKey, isOpen)
                  }
                  onCommentAdded={onCommentAdded}
                  requestProgress={requestProgress}
                  requestDateEnded={requestDateEnded}
                  currentUserEmail={currentUserEmail}
                  onCommentUpdated={onCommentUpdated}
                  onCommentDeleted={onCommentDeleted}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {isHiddenField && (
        <div className={styles.hiddenFieldNotice}>
          <Typography variant="caption" color="textSecondary">
            {t('formRenderer.hiddenFieldNotice')}
          </Typography>
        </div>
      )}

      {activeQuickCommentField === fieldKey && enableAddComment && (
        <div className={styles.quickCommentSlot}>
          <QuickCommentInput
            subformId={subformId}
            fieldId={fieldIdOverride || field.id}
            onAddComment={onAddComment}
            onClose={onCloseQuickComment}
            onCommentAdded={onCommentAdded}
          />
        </div>
      )}
    </div>
  );
};

export default FieldRow;
